import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const [settings, setSettings] = useState({ max_concurrent_calls: 3 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, settings);
      setSettings(response.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 mt-1">Configure your campaign dialer preferences</p>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm max-w-lg">
          <CardHeader>
            <CardTitle className="text-gray-900">Call Queue</CardTitle>
            <CardDescription className="text-gray-500">
              Control how many calls run at the same time in bulk campaigns.
              Remaining calls stay queued until a slot opens up.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="h-10 bg-gray-100 rounded animate-pulse" />
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="max-calls" className="text-gray-700">
                    Max Concurrent Calls
                  </Label>
                  <Input
                    id="max-calls"
                    type="number"
                    min={1}
                    max={10}
                    value={settings.max_concurrent_calls}
                    onChange={(e) =>
                      setSettings({ ...settings, max_concurrent_calls: parseInt(e.target.value) || 1 })
                    }
                    className="bg-white border-gray-300 text-gray-900 w-32"
                  />
                  <p className="text-xs text-gray-500">
                    Between 1 and 10. New bulk campaigns will use this value.
                  </p>
                </div>
                <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
